import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  History,
  RefreshCcw,
  LogIn,
  LogOut,
  Search,
  Users,
  Building2,
  Loader2,
  Monitor,
  Smartphone,
  MousePointerClick,
  LayoutGrid,
} from 'lucide-react';

const ADMIN_EMAIL = 'adpenia@gmail.com';

const TABS = [
  { id: 'sesiones', label: 'Sesiones', icon: LogIn },
  { id: 'navegacion', label: 'Navegación', icon: MousePointerClick },
];

const EVENT_FILTERS = [
  { id: 'all', label: 'Todos' },
  { id: 'login', label: 'Inicios' },
  { id: 'logout', label: 'Cierres' },
];

const PERIODS = [
  { id: '24h', label: '24h', hours: 24 },
  { id: '7d', label: '7 días', hours: 24 * 7 },
  { id: '30d', label: '30 días', hours: 24 * 30 },
  { id: 'all', label: 'Todo', hours: null },
];

const PAGE_SIZE = 200;

const formatAbsolute = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diffMs)) return '—';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'recién';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 30) return `hace ${days} d`;
  const months = Math.round(days / 30);
  return `hace ${months} mes${months > 1 ? 'es' : ''}`;
};

// 'ventas-pos' -> 'Ventas Pos' (legible sin tener que mantener un mapa enorme).
const prettyModule = (moduleId) => {
  if (!moduleId) return '—';
  return String(moduleId)
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Resumen legible del navegador/SO a partir del user agent.
const summarizeUserAgent = (ua) => {
  if (!ua) return { label: '—', mobile: false };
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  let browser = 'Navegador';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/OPR\//i.test(ua) || /Opera/i.test(ua)) browser = 'Opera';
  else if (/Chrome\//i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua)) browser = 'Safari';

  let os = '';
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iOS/i.test(ua)) os = 'iOS';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  return { label: [browser, os].filter(Boolean).join(' · '), mobile };
};

const EventBadge = ({ type }) => {
  const isLogin = type === 'login';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider ${
        isLogin ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'
      }`}
    >
      {isLogin ? <LogIn size={11} /> : <LogOut size={11} />}
      {isLogin ? 'Inicio' : 'Cierre'}
    </span>
  );
};

export default function AuditoriaSesiones() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = String(user?.email || '').toLowerCase() === ADMIN_EMAIL;

  const [tab, setTab] = useState('sesiones');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventFilter, setEventFilter] = useState('all');
  const [period, setPeriod] = useState('7d');
  const [search, setSearch] = useState('');
  const [empresaFilter, setEmpresaFilter] = useState('all');
  const [rolFilter, setRolFilter] = useState('all');

  const isNav = tab === 'navegacion';

  const loadLogs = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const table = isNav ? 'navigation_logs' : 'auth_session_logs';
      const columns = isNav
        ? 'id, user_id, email, full_name, organization_name, role, module_id, path, created_at'
        : 'id, user_id, email, full_name, organization_name, role, event_type, user_agent, created_at';

      let query = supabase
        .from(table)
        .select(columns)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      const periodDef = PERIODS.find((p) => p.id === period);
      if (periodDef?.hours) {
        const since = new Date(Date.now() - periodDef.hours * 3600 * 1000).toISOString();
        query = query.gte('created_at', since);
      }

      if (!isNav && eventFilter !== 'all') {
        query = query.eq('event_type', eventFilter);
      }

      const { data, error: queryError } = await query;
      if (queryError) throw queryError;
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[AuditoriaSesiones] error cargando bitácora', err);
      setError(err?.message || 'No se pudo cargar la bitácora');
      toast.error('No se pudo cargar la bitácora');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isNav, eventFilter, period]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/sistema/dashboard', { replace: true });
      return;
    }
    loadLogs();
  }, [isAdmin, loadLogs, navigate]);

  // Opciones de empresa y rol derivadas de los eventos cargados.
  const empresaOptions = useMemo(() => {
    const set = new Set(logs.map((l) => l.organization_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [logs]);

  const rolOptions = useMemo(() => {
    const set = new Set(logs.map((l) => l.role).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (empresaFilter !== 'all' && (l.organization_name || '') !== empresaFilter) return false;
      if (rolFilter !== 'all' && (l.role || '') !== rolFilter) return false;
      if (term) {
        const match = [l.email, l.full_name, l.organization_name, l.role, l.module_id]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term));
        if (!match) return false;
      }
      return true;
    });
  }, [logs, search, empresaFilter, rolFilter]);

  const stats = useMemo(() => {
    const uniqueUsers = new Set(logs.map((l) => l.user_id).filter(Boolean)).size;

    if (isNav) {
      const counts = {};
      logs.forEach((l) => {
        if (!l.module_id) return;
        counts[l.module_id] = (counts[l.module_id] || 0) + 1;
      });
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      return {
        total: logs.length,
        uniqueUsers,
        uniqueModules: Object.keys(counts).length,
        topModule: top ? prettyModule(top[0]) : '—',
      };
    }

    return {
      total: logs.length,
      logins: logs.filter((l) => l.event_type === 'login').length,
      logouts: logs.filter((l) => l.event_type === 'logout').length,
      uniqueUsers,
    };
  }, [logs, isNav]);

  if (!isAdmin) return null;

  return (
    <div className="imp-page imp-container animate-nova-in px-3 py-3 sm:px-4 sm:py-4 lg:px-5">
      {/* Header */}
      <div className="imp-header mb-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <History size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              Auditoría de Sesiones
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {isNav
                ? 'Registro de clics en los módulos del sidebar'
                : 'Registro de inicios y cierres de sesión de todos los usuarios'}
            </p>
          </div>
        </div>

        <div className="imp-header-actions w-full md:w-auto flex items-center gap-2">
          <button
            onClick={loadLogs}
            disabled={loading}
            className="px-4 py-2.5 rounded-2xl bg-[#0B2A36] text-white shadow-md hover:shadow-lg transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RefreshCcw size={14} className={loading ? 'animate-spin' : ''} />
            Recargar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-2xl text-[11px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
                active
                  ? 'bg-[#0B2A36] text-white shadow-md'
                  : 'bg-white border border-slate-200 text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {isNav ? (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Clics</p>
              <p className="text-3xl font-black text-slate-800">{stats.total}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-sky-500 mb-1">Usuarios</p>
              <p className="text-3xl font-black text-slate-800">{stats.uniqueUsers}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-violet-500 mb-1">Módulos distintos</p>
              <p className="text-3xl font-black text-slate-800">{stats.uniqueModules}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-1">Más usado</p>
              <p className="text-lg font-black text-slate-800 truncate" title={stats.topModule}>{stats.topModule}</p>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Eventos</p>
              <p className="text-3xl font-black text-slate-800">{stats.total}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-500 mb-1">Inicios</p>
              <p className="text-3xl font-black text-slate-800">{stats.logins}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-500 mb-1">Cierres</p>
              <p className="text-3xl font-black text-slate-800">{stats.logouts}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-sky-500 mb-1">Usuarios</p>
              <p className="text-3xl font-black text-slate-800">{stats.uniqueUsers}</p>
            </div>
          </>
        )}
      </div>

      {/* Filtros */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 mb-4 shadow-sm flex flex-wrap gap-2 items-center">
        {!isNav && (
          <div className="flex items-center gap-1 flex-wrap">
            {EVENT_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setEventFilter(f.id)}
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${
                  eventFilter === f.id
                    ? 'bg-[#0B2A36] text-white shadow-md'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 flex-1 min-w-[180px] max-w-sm bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5">
          <Search size={15} className="text-slate-400 shrink-0" />
          <input
            className="bg-transparent outline-none w-full text-sm"
            placeholder={isNav ? 'Buscar por usuario, empresa, rol o módulo…' : 'Buscar por usuario, email, empresa o rol…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          value={empresaFilter}
          onChange={(e) => setEmpresaFilter(e.target.value)}
          className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD] max-w-[180px]"
        >
          <option value="all">Todas las empresas</option>
          {empresaOptions.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        <select
          value={rolFilter}
          onChange={(e) => setRolFilter(e.target.value)}
          className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD] max-w-[150px] capitalize"
        >
          <option value="all">Todos los roles</option>
          {rolOptions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        <div className="flex items-center gap-1 ml-auto">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${
                period === p.id
                  ? 'bg-[#0B2A36] text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Listado */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        {loading && (
          <div className="p-10 flex items-center justify-center text-slate-400">
            <Loader2 size={20} className="animate-spin mr-2" />
            <span className="text-[11px] font-bold uppercase tracking-widest">Cargando bitácora…</span>
          </div>
        )}

        {!loading && error && (
          <div className="p-6 text-center">
            <p className="text-sm font-bold text-red-600 mb-2">No se pudo cargar la bitácora</p>
            <p className="text-xs text-slate-500">{error}</p>
          </div>
        )}

        {!loading && !error && filteredLogs.length === 0 && (
          <div className="p-10 text-center text-slate-400">
            <p className="text-sm font-bold mb-1">Sin registros en este período</p>
            <p className="text-xs">Probá ampliar el rango o cambiar el filtro.</p>
          </div>
        )}

        {!loading && !error && filteredLogs.length > 0 && (
          <>
            {/* Encabezado (desktop) */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-100">
              <div className="col-span-3">Usuario</div>
              <div className="col-span-3">Empresa</div>
              <div className="col-span-2">Rol</div>
              <div className="col-span-2">{isNav ? 'Módulo' : 'Evento'}</div>
              <div className="col-span-2 text-right">Fecha</div>
            </div>

            <ul className="divide-y divide-slate-100">
              {filteredLogs.map((log) => {
                const device = isNav ? null : summarizeUserAgent(log.user_agent);
                return (
                  <li
                    key={log.id}
                    className="px-4 py-3 hover:bg-slate-50 transition-colors grid grid-cols-1 md:grid-cols-12 gap-1.5 md:gap-2 md:items-center"
                  >
                    {/* Usuario (en sesiones, dispositivo como tercera línea) */}
                    <div className="md:col-span-3 min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate">
                        {log.full_name || '—'}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                        <Users size={11} className="shrink-0" />
                        {log.email || '—'}
                      </p>
                      {device && (
                        <p className="text-[10px] text-slate-400 truncate flex items-center gap-1" title={log.user_agent || ''}>
                          {device.mobile
                            ? <Smartphone size={11} className="shrink-0" />
                            : <Monitor size={11} className="shrink-0" />}
                          {device.label}
                        </p>
                      )}
                    </div>

                    {/* Empresa */}
                    <div className="md:col-span-3 min-w-0">
                      <p className="text-[12px] text-slate-600 truncate flex items-center gap-1">
                        <Building2 size={12} className="shrink-0 text-slate-400" />
                        {log.organization_name || '—'}
                      </p>
                    </div>

                    {/* Rol */}
                    <div className="md:col-span-2 min-w-0">
                      {log.role ? (
                        <span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-wider truncate max-w-full">
                          {log.role}
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400">—</span>
                      )}
                    </div>

                    {/* Módulo / Evento */}
                    <div className="md:col-span-2 min-w-0">
                      {isNav ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 text-[10px] font-black uppercase tracking-wider truncate max-w-full"
                          title={log.module_id || ''}
                        >
                          <LayoutGrid size={11} className="shrink-0" />
                          {prettyModule(log.module_id)}
                        </span>
                      ) : (
                        <EventBadge type={log.event_type} />
                      )}
                    </div>

                    {/* Fecha */}
                    <div className="md:col-span-2 md:text-right">
                      <p className="text-[12px] font-bold text-slate-700">{formatAbsolute(log.created_at)}</p>
                      <p className="text-[10px] text-slate-400">{formatRelative(log.created_at)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {logs.length >= PAGE_SIZE && (
              <div className="px-4 py-3 text-center text-[11px] text-slate-400 border-t border-slate-100">
                Mostrando los {PAGE_SIZE} eventos más recientes del período. Acotá el rango para ver registros más antiguos.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
